import { Component, inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-deep-link-redirect',
  standalone: false,
  templateUrl: './deep-link-redirect.component.html',
  styleUrls: ['./deep-link-redirect.component.scss']
})
export class DeepLinkRedirectComponent implements OnInit {
  protected route = inject(ActivatedRoute);

  @ViewChild('deepLinkButton') deepLinkButton!: ElementRef<HTMLButtonElement>;

  appConfig: any;

  constructor() {
    this.appConfig =
      JSON.parse((window as any).app_config || '{}').apps || {};
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.deepLinkButton?.nativeElement?.click();
    }, 100);
  }

  private isIOS(): boolean {
    return /iphone|ipad|ipod/i.test(navigator.userAgent);
  }

  private isAndroid(): boolean {
    return /android/i.test(navigator.userAgent);
  }

  private isHuaweiDevice(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    const isHuaweiUA = /huawei|honor/.test(ua);
    const isHmsCore = /hmscore|hw/.test(ua);
    const hasGMS = !!(window as any).gapi || !!(window as any).google;

    return (isHuaweiUA || isHmsCore) && !hasGMS;
  }

  protected attemptDeepLink(): void {
    if (this.isIOS()) return this.openIOS();

    if (this.isAndroid()) {
      return this.isHuaweiDevice()
        ? this.openHuawei()
        : this.openAndroid();
    }

    return this.openStore();
  }

  // private openIOS(): void {
  //   const start = Date.now();
  //   let appOpened = false;

  //   const SCHEME = (window as any).deep_link_ios_scheme || '';
  //   if (!SCHEME) {
  //     this.openStore();
  //     return;
  //   }

  //   const deepLinkUrl = `${SCHEME}://`;

  //   let fallbackTimer: any;

  //   const cleanup = () => {
  //     document.removeEventListener('visibilitychange', onVisibilityChange);
  //     window.removeEventListener('blur', onBlurOrPageHide);
  //     window.removeEventListener('pagehide', onBlurOrPageHide);
  //   };

  //   const markAppOpened = () => {
  //     appOpened = true;
  //     // clearTimeout(fallbackTimer);
  //     cleanup();
  //   };

  //   const onVisibilityChange = () => {
  //     if (document.hidden) {
  //       markAppOpened();
  //     }
  //   };

  //   const onBlurOrPageHide = () => {
  //     // const diff = Date.now() - start;
  //     // if (diff > 1000) {
  //     //   markAppOpened();
  //     // }
  //   };

  //   document.addEventListener('visibilitychange', onVisibilityChange);
  //   window.addEventListener('blur', onBlurOrPageHide);
  //   window.addEventListener('pagehide', onBlurOrPageHide);

  //   // Trigger the deep link
  //   setTimeout(() => {
  //     window.location.href = deepLinkUrl;
  //   }, 120);

  //   // Fallback to App Store
  //   fallbackTimer = setTimeout(() => {
  //     cleanup();

  //     if (!appOpened && !document.hidden) {
  //       this.openStore();
  //     }
  //   }, 2000);
  // }

  private openIOS(): void {
    let appOpened = false;
    let startTime = Date.now();
    let fallbackTimer: any;

    const cleanup = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pagehide', handleBlur);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        clearTimeout(fallbackTimer);
        cleanup();
      }
    };

    const handleBlur = () => { };

    const handleFocus = () => {
      // Check if enough time has passed (app was likely opened)
      let timeDiff = Date.now() - startTime;
      if (timeDiff > 1000) {
        appOpened = true;
        clearTimeout(fallbackTimer);
        cleanup();
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pagehide', handleBlur);

    setTimeout(() => {
      try {
        const SCHEME = (window as any).deep_link_ios_scheme || '';
        const deepLinkUrl = `${SCHEME}://npss`;
        window.location.href = deepLinkUrl;
      } catch (e) {
        this.openStore();
        return;
      }
    }, 1500);

    fallbackTimer = setTimeout(() => {
      cleanup();

      if (!appOpened && !document.hidden) {
        this.openStore();
      }
    }, 2000);
  }

  private openAndroid(): void {
    let appOpened = false;
    let timer: any;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        clearTimeout(timer);
      }
    };

    const handleBlur = () => {
      appOpened = true;
      clearTimeout(timer);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handleBlur);

    try {
      const cfg: any = window as any;

      const scheme = cfg.deep_link_android_scheme;
      const host = cfg.deep_link_android_host;
      const path = cfg.deep_link_android_path_prefix || '';
      const packageName = cfg.deep_link_android_package;

      const androidStoreUrl = this.appConfig?.android;
      const fallbackUrl = encodeURIComponent(androidStoreUrl);

      const intentUrl =
        `intent://${host}${path}#Intent;` +
        `scheme=${scheme};` +
        `package=${packageName};` +
        `S.browser_fallback_url=${fallbackUrl};` +
        `end`;

      window.location.href = intentUrl;
    } catch (e) {
      this.openStore();
      return;
    }

    timer = setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handleBlur);

      if (!appOpened) {
        this.openStore();
      }
    }, 2500);

    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handleBlur);
    }, 3000);
  }

  private openHuawei(): void {
    let appOpened = false;
    let timer: any;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        clearTimeout(timer);
      }
    };

    const handleBlur = () => {
      appOpened = true;
      clearTimeout(timer);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handleBlur);

    try {
      const cfg: any = window as any;
      const pkg = cfg.deep_link_android_package;

      const huaweiStoreUrl = this.appConfig?.huawei || this.appConfig?.android;
      const fallback = encodeURIComponent(huaweiStoreUrl);

      const marketIntent =
        `intent://details?id=${pkg}#Intent;` +
        `scheme=appmarket;` +
        `action=android.intent.action.VIEW;` +
        `package=com.huawei.appmarket;` +
        `S.browser_fallback_url=${fallback};` +
        `end`;

      window.location.href = marketIntent;
    } catch (e) {
      this.openStore();
      return;
    }

    timer = setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handleBlur);

      if (!appOpened) {
        this.openStore();
      }
    }, 2500);

    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handleBlur);
    }, 3000);
  }

  openStore(): void {
    if (this.isIOS()) {
      return (window.location.href = this.appConfig?.ios);
    }

    if (this.isHuaweiDevice()) {
      return (window.location.href =
        this.appConfig?.huawei || this.appConfig?.android);
    }

    if (this.isAndroid()) {
      return (window.location.href = this.appConfig?.android);
    }
  }
}
